/** QuickRouter — Classes de nets à impédance contrôlée.
 *
 * Un net prend sa géométrie de : sa largeur imposée (width) > son impédance (impedance, ou celle
 * de sa classe) calculée sur l'empilage de la carte > la règle de largeur (signal / puissance).
 * Un net qui a un partenaire (pair) est une paire différentielle : l'impédance est alors
 * différentielle et l'écart entre les deux pistes en découle.
 */
import { synthesize } from './stackup.js';

export const NET_CLASSES = {
  USB_SS: { name: 'USB 3.0 SuperSpeed', impedance: 90, diff: true, skew: 0.13, note: 'condensateurs de liaison 100 nF sur TX, ≤ 2 vias par paire avec via de masse' },
  USB_HS: { name: 'USB 2.0', impedance: 90, diff: true, skew: 1.25 },
  ETH_MDI: { name: 'Ethernet MDI', impedance: 100, diff: true, skew: 0.13, note: '≤ 2,5 mm d\'écart entre paires' },
  LVDS: { name: 'LVDS / MIPI D-PHY', impedance: 100, diff: true, skew: 0.15 },
  SE50: { name: 'Simple 50 Ω', impedance: 50, diff: false },
};

const cache = new Map();

/** Règle géométrique d'un net sur une couche : { width, gap?, impedance?, diff, skew?, cls? } ou null. */
export function netRule(project, net, layer = 'F') {
  if (!net) return null;
  const cls = net.netclass ? NET_CLASSES[net.netclass] : null;
  const z = net.impedance ?? cls?.impedance;
  const diff = !!net.pair || !!cls?.diff;
  if (!z) return net.width ? { width: net.width, diff } : null;
  const b = project.board;
  const key = `${b.layers}|${b.stackup}|${layer}|${z}|${diff}|${project.rules.clearance}`;
  if (!cache.has(key)) {
    const minWidth = Math.min(project.rules.trackWidth, 0.09);
    cache.set(key, synthesize(b, layer, z, { diff, minGap: Math.max(0.09, Math.min(project.rules.clearance, 0.127)), minWidth }));
    if (cache.size > 500) cache.delete(cache.keys().next().value);
  }
  const r = cache.get(key);
  return {
    width: net.width ?? r.w,
    gap: diff ? r.s : undefined,
    impedance: z,
    achieved: r.z,
    diff,
    skew: cls?.skew ?? project.rules.pairSkew,
    cls: net.netclass,
  };
}
